import { TransitionDto } from "@repo/api/transition";
import { useCallback, useState } from "react";
import TransitionTriggerButton from "./TransitionTriggerButton";
import TransitionDialog from "./TransitionDialog";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import {
  ImageTransitionBuilders,
  TransitionFormConfig,
} from "@repo/video-engine-i2v/components/ImageTransitionBuilder/ImageTransitionBuilder";
import { UseTransitionPreview } from "./TransitionPreview";
import ImageTransitionComposition, {
  ImageTransitionCompositionProps,
} from "@repo/video-engine-i2v/ImageTransitionComposition";
import { CreativeProjectTimelineItem } from "@repo/api/creative-project";

type Props = {
  timelineItem: CreativeProjectTimelineItem;
};

const transitionFormConfigs: TransitionFormConfig[] = [
  ImageTransitionBuilders.get("NONE")!.formConfig,
  ImageTransitionBuilders.get("FADE")!.formConfig,
  ImageTransitionBuilders.get("FADE_BLACK")!.formConfig,
  ImageTransitionBuilders.get("FADE_WHITE")!.formConfig,
  ImageTransitionBuilders.get("PUSH")!.formConfig,
  ImageTransitionBuilders.get("WIPE")!.formConfig,
  ImageTransitionBuilders.get("ZOOM_IN_FADE")!.formConfig,
  ImageTransitionBuilders.get("ZOOM_IN_OUT")!.formConfig,
  ImageTransitionBuilders.get("ZOOM_BLUR")!.formConfig,
  ImageTransitionBuilders.get("ZOOM_OUT_BLUR")!.formConfig,
  ImageTransitionBuilders.get("GLITCH_CUT")!.formConfig,
  ImageTransitionBuilders.get("LIGHT_LEAK")!.formConfig,
  ImageTransitionBuilders.get("FOCUS_PULL")!.formConfig,
  ImageTransitionBuilders.get("WHIP_PAN")!.formConfig,
  ImageTransitionBuilders.get("CLOCK_WIPE")!.formConfig,
  ImageTransitionBuilders.get("SPLIT_REVEAL")!.formConfig,
  ImageTransitionBuilders.get("SPLIT_SCREEN_WIPE")!.formConfig,
];

const useImageTransitionPreview: UseTransitionPreview = (
  transition: TransitionDto,
) => {
  const fps = 30;
  const imageTransitionBuilder = ImageTransitionBuilders.get(transition.kind);

  if (!imageTransitionBuilder) {
    throw new Error(
      `ImageTransitionBuilder not found for kind: ${transition.kind}`,
    );
  }

  const transitionDurationInFrames =
    imageTransitionBuilder.transition?.getDurationInFrames({
      imageTransition: transition,
      fps,
    }) ?? 0;

  const { previewClip1DurationInFrames, previewClip2DurationInFrames } =
    imageTransitionBuilder.getPreviewClipDurationsInFrames({
      imageTransition: transition,
      fps,
    });

  const inputProps: ImageTransitionCompositionProps = {
    transition,
    clip1DurationInFrames: previewClip1DurationInFrames,
    clip2DurationInFrames: previewClip2DurationInFrames,
  };

  const durationInFrames =
    previewClip1DurationInFrames +
    previewClip2DurationInFrames -
    transitionDurationInFrames;

  return {
    Composition: ImageTransitionComposition,
    durationInFrames,
    fps,
    inputProps,
  };
};

export default function ImageTransitionDialog({ timelineItem }: Props) {
  const [dialogKey, setDialogKey] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const updateVideo = useEditorStore((state) => state.service.updateVideo);

  const setTransition = useCallback(
    (transition: TransitionDto) => {
      updateVideo((video) => {
        const timelineItemToEdit = video.compositionJson.mainSceneTimeline.find(
          (item) => item.externalId === timelineItem.externalId,
        )!;

        timelineItemToEdit.entryTransition = transition;
      });
    },
    [updateVideo, timelineItem],
  );

  const setTransitionAll = useCallback(
    (transition: TransitionDto) => {
      updateVideo((video) => {
        video.compositionJson.mainSceneTimeline.forEach((item) => {
          item.entryTransition = transition;
        });
      });
    },
    [updateVideo],
  );

  const noneTransition =
    ImageTransitionBuilders.get("NONE")!.formConfig.buildDefaultValue();

  const hasTransition =
    timelineItem.entryTransition != null &&
    timelineItem.entryTransition.kind !== "NONE";

  const openDialog = () => {
    setDialogKey((key) => key + 1); // Hack to reset dialog state when it's opened
    setIsOpen(true);
  };

  return (
    <div
      className="absolute right-0 bottom-full left-0 z-1 ml-8 flex h-3 items-center justify-center"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <TransitionTriggerButton
        hasTransition={hasTransition}
        onClick={openDialog}
      />

      <TransitionDialog
        key={`transition-dialog-${dialogKey}`}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        currentTransition={timelineItem.entryTransition ?? noneTransition}
        onTransitionChange={setTransition}
        onTransitionChangeAll={setTransitionAll}
        transitionFormConfigs={transitionFormConfigs}
        useTransitionPreview={useImageTransitionPreview}
      />
    </div>
  );
}
